import type { FloorPlanTableData } from '@shared/types/floorPlans';

export const getFloorPlanTableIdsForListings = (
  floorPlanTables: FloorPlanTableData[],
  listingIds: string[],
): string[] => {
  if (listingIds.length === 0) {
    return [];
  }

  const listingIdSet = new Set(listingIds);

  return floorPlanTables
    .filter((table) =>
      table.listings.some((listing) => listingIdSet.has(listing.id)),
    )
    .map((table) => table.id);
};

// A physical table can simultaneously belong to its own single-table listing
// and to a combo listing that is also valid for the current guest count
// (e.g. a 4-top combo can still be booked by a party of 1). A combination is
// only actually needed when the party is bigger than what this one physical
// table can seat on its own — so prefer the table's own (non-combo) listing
// unless the selected guest count exceeds what this table alone can seat, or
// unless no non-combo listing is active for it.
export const getComboAwareActiveListingIds = (
  floorPlanTables: FloorPlanTableData[],
  table: FloorPlanTableData,
  activeListingIds: string[],
  selectedGuestCount: number,
): string[] => {
  if (selectedGuestCount <= table.maximumGuests) {
    const soloListingId = activeListingIds.find(
      (listingId) =>
        getFloorPlanTableIdsForListings(floorPlanTables, [listingId]).length ===
        1,
    );

    if (soloListingId) {
      return [soloListingId];
    }
  }

  return activeListingIds;
};

export const getComboAwareTableIds = (
  floorPlanTables: FloorPlanTableData[],
  table: FloorPlanTableData,
  activeListingIds: string[],
  selectedGuestCount: number,
): string[] =>
  getFloorPlanTableIdsForListings(
    floorPlanTables,
    getComboAwareActiveListingIds(
      floorPlanTables,
      table,
      activeListingIds,
      selectedGuestCount,
    ),
  );
